import { videoEditor } from '@mi-front/api-clients';
import { LocaleRelativeTime } from '@mi-front/ui';
import EditIcon from '@mui/icons-material/Edit';
import VideoFileIcon from '@mui/icons-material/VideoFile';
import {
  Box,
  Chip,
  FormControl,
  InputLabel,
  ListItemIcon,
  ListItemText,
  MenuItem,
  Select,
  SelectChangeEvent,
} from '@mui/material';
import { ReactNode } from 'react';
import { useIntl } from 'react-intl';

export const MEDIA_SOURCE_FRAGMENT = 'fragment';

export interface SocialExportMediaSelectorProps {
  /** Current selection: MEDIA_SOURCE_FRAGMENT or a project id */
  value: string;
  /** Callback when user selects a different option */
  onChange: (value: string) => void;
  /** List of video-editor projects for this fragment (only rendered options with completed render should be selectable if needed) */
  projects: videoEditor.Project[];
}

/** Content for a single option row: icon, primary text, and secondary (chip and optional extra). */
interface OptionRowContent {
  icon: ReactNode;
  primary: ReactNode;
  secondary: ReactNode;
}

/** Renders the same option row layout used in both the dropdown and the select trigger. */
function renderOptionRow(content: OptionRowContent, compact: boolean): ReactNode {
  const inner = (
    <>
      <ListItemIcon sx={compact ? { minWidth: 36 } : undefined}>{content.icon}</ListItemIcon>
      <ListItemText
        primary={content.primary}
        secondary={content.secondary}
        sx={compact ? { my: 0 } : undefined}
      />
    </>
  );
  return compact ? (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>{inner}</Box>
  ) : (
    inner
  );
}

/**
 * MUI Select to choose between base fragment media and video-edition projects for social export.
 * Shows fragment option with VideoFile icon; each project with Edit icon and updatedAt as subtitle.
 */
export function SocialExportMediaSelector({
  value,
  onChange,
  projects,
}: SocialExportMediaSelectorProps) {
  const { $t } = useIntl();

  const handleChange = (event: SelectChangeEvent<string>) => {
    onChange(event.target.value);
  };

  const fragmentContent: OptionRowContent = {
    icon: <VideoFileIcon fontSize="small" />,
    primary: $t({
      id: 'fragments.socialExport.mediaSelector.fragmentOption',
      defaultMessage: 'Media original',
    }),
    secondary: '',
  };

  const getProjectContent = (project: videoEditor.Project): OptionRowContent => ({
    icon: <EditIcon fontSize="small" />,
    primary: (
      <Box
        sx={{
          display: 'flex',
          flexWrap: 'wrap',
          alignItems: 'center',
          gap: 1,
          my: 0.5,
        }}
      >
        {project.name}
        <Chip
          size="small"
          variant="outlined"
          label={$t({
            id: 'fragments.socialExport.mediaSelector.edition',
            defaultMessage: 'Edición',
          })}
          sx={{ fontSize: '11px' }}
        />
      </Box>
    ),
    secondary:
      project.renderJob?.status === videoEditor.publicJobWithResultDtoStatusEnum.COMPLETED &&
      project.renderJob.finishedOn ? (
        <>
          {$t({
            id: 'fragments.videoEditions.finishedOn',
            defaultMessage: 'Generado',
          })}{' '}
          <LocaleRelativeTime value={project.renderJob.finishedOn} />
        </>
      ) : (
        <LocaleRelativeTime value={project.updatedAt} />
      ),
  });

  const label = $t({
    id: 'fragments.socialExport.mediaSelector.label',
    defaultMessage: 'Video a publicar',
  });

  const renderedProjects = projects.filter(
    (project) =>
      project.renderJob?.status === videoEditor.publicJobWithResultDtoStatusEnum.COMPLETED,
  );

  return (
    <FormControl fullWidth sx={{ minWidth: 280 }}>
      <InputLabel id="social-export-media-selector-label">{label}</InputLabel>
      <Select
        labelId="social-export-media-selector-label"
        value={value}
        label={label}
        onChange={handleChange}
        renderValue={(v) => {
          if (v === MEDIA_SOURCE_FRAGMENT) return renderOptionRow(fragmentContent, true);
          const p = renderedProjects.find((x) => x.id === v);
          if (!p) return v;
          return renderOptionRow(getProjectContent(p), true);
        }}
      >
        <MenuItem value={MEDIA_SOURCE_FRAGMENT} sx={{ minHeight: '56px !important' }}>
          {renderOptionRow(fragmentContent, false)}
        </MenuItem>
        {renderedProjects.map((project) => (
          <MenuItem key={project.id} value={project.id} sx={{ minHeight: 56 }}>
            {renderOptionRow(getProjectContent(project), false)}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
}
